import { Function_ } from "@/types";
import { isBuiltInTool, isMcpTool } from "@/utils/utils";

const getDeclarationMetadata = (declaration: Function_) => {
  if (isMcpTool(declaration)) {
    return {
      name: declaration._resolved_name || "Unavailable MCP Server",
      description: "Tools provided by a centralized MCP server.",
    };
  }
  return { name: declaration.name, description: declaration.description };
};

const getOptionalParameters = (declaration: Function_): string[] => {
  if (isBuiltInTool(declaration) || isMcpTool(declaration)) {
    return [];
  }
  const requiredParameters = declaration?.parameters?.required || [];
  const allParameters = Object.keys(declaration?.parameters?.properties || {});
  const optionalParameters = allParameters.filter(param => !requiredParameters.includes(param));
  return optionalParameters;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ObjectParameterRenderer({ properties }: { properties: any }) {
  return (
    <ul className="py-1 pl-4">
      {Object.keys(properties).map(nestedKey => (
        <ParameterRenderer key={nestedKey} keyName={nestedKey} value={properties[nestedKey]} />
      ))}
    </ul>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ArrayParameterRenderer({ items }: { items: any }) {
  return (
    <ul className="py-1 pl-4">
      {typeof items === "string" ? (
        <li>{items}</li>
      ) : (
        <>
          {items.description && (
            <span className="font-sans text-sm italic text-gray-500">
              {items.description.trim()}
            </span>
          )}
          {items.properties && <ObjectParameterRenderer properties={items.properties} />}
        </>
      )}
    </ul>
  );
}

// Component to render individual parameters
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ParameterRenderer({ keyName, value }: { keyName: string; value: any }) {
  return (
    <li className="pb-1 text-sm">
      <span className="font-semibold text-gray-500">{`${keyName} (${value?.type})`}</span>
      {value?.enum && (
        <span className="font-mono text-sm font-semibold text-gray-500">
          {Array.isArray(value?.enum) ? value?.enum.join(", ") : `{{${value.enum.name}}}`}
        </span>
      )}
      {value?.description && (
        <span className="font-sans text-sm italic text-gray-500">
          : {value?.description?.trim()}
        </span>
      )}
      {value?.type === "object" && value?.properties && (
        <ObjectParameterRenderer properties={value.properties} />
      )}
      {value?.type === "array" && value?.items && <ArrayParameterRenderer items={value.items} />}
    </li>
  );
}

// Component to render parameters list
const ParametersList = ({
  parameters,
  properties,
  title,
}: {
  parameters: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties: Record<string, any>;
  title: string;
}) =>
  parameters.length > 0 && (
    <>
      <div className="font-sans text-sm text-gray-900">{title}</div>
      <ul className="py-1 pl-4">
        {parameters.map(param => (
          <ParameterRenderer key={param} keyName={param} value={properties[param]} />
        ))}
      </ul>
    </>
  );

const DeclarationParameters = ({ declaration }: { declaration: Function_ }) => {
  if (isBuiltInTool(declaration) || isMcpTool(declaration)) {
    return null;
  }

  if (declaration?.parameters?.type !== "object") {
    return <div>{JSON.stringify(declaration, undefined, 2)}</div>;
  }

  return (
    <>
      <ParametersList
        parameters={declaration?.parameters?.required || []}
        properties={declaration?.parameters?.properties || {}}
        title="Required Parameters:"
      />
      <ParametersList
        parameters={getOptionalParameters(declaration)}
        properties={declaration?.parameters?.properties || {}}
        title="Optional Parameters:"
      />
    </>
  );
};

export const FunctionDeclarationsFormatter = ({
  functionDeclarations,
  functionCall,
}: {
  functionDeclarations: Function_[];
  functionCall?: string | Record<string, string>;
}) => (
  <div className="space-y-2">
    {functionDeclarations.map((declaration: Function_) => {
      const metadata = getDeclarationMetadata(declaration);
      return (
        <div key={metadata.name || JSON.stringify(declaration)}>
          <div className="font-medium text-gray-900">{metadata.name}</div>
          <div className="pl-2 pt-1 font-sans text-sm italic text-gray-500">
            {metadata.description}
          </div>
          <div className="pl-2 pt-1 text-gray-600">
            <DeclarationParameters declaration={declaration} />
          </div>
        </div>
      );
    })}
    {functionCall !== "" ? (
      <div className="font-medium text-gray-700">
        {"function_call: "}
        {typeof functionCall === "string" ? (
          <span className="italic">{functionCall}</span>
        ) : (
          <span className="font-mono">{functionCall?.name}</span>
        )}
      </div>
    ) : null}
  </div>
);
